import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  LockOutlined as LockIcon,
  EmailOutlined as EmailIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const { signIn, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await signIn(email, password);
      localStorage.removeItem('isGuest'); // Clear guest flag on admin login
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || t("auth.login.errorFallback"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setResetError(null);
    setResetSuccess(false);
    setResetLoading(true);

    try {
      await resetPassword(resetEmail);
      setResetSuccess(true);
    } catch (err) {
      setResetError(err.message || t("auth.forgotPassword.error"));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)',
        pt: 8
      }}
    >
      <Container maxWidth="xs">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper 
            elevation={24}
            sx={{ 
              p: 4, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 4
            }}
          >
            <Box 
              sx={{ 
                p: 1.5, 
                bgcolor: 'primary.main', 
                borderRadius: '50%', 
                mb: 2,
                boxShadow: '0 0 20px rgba(15, 163, 163, 0.5)'
              }}
            >
              <LockIcon sx={{ color: 'white' }} />
            </Box>

            {forgotMode ? (
              <>
                <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white', fontWeight: 700, textAlign: 'center' }}>
                  {t("auth.forgotPassword.title")}
                </Typography>
                <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3, textAlign: 'center' }}>
                  {t("auth.forgotPassword.subtitle")}
                </Typography>

                {resetSuccess && (
                  <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
                    {t("auth.forgotPassword.success")}
                  </Alert>
                )}
                {resetError && (
                  <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                    {resetError}
                  </Alert>
                )}

                {!resetSuccess && (
                  <Box component="form" onSubmit={handleForgotSubmit} sx={{ width: '100%' }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="reset-email"
                      label={t("auth.forgotPassword.emailLabel")}
                      name="email"
                      autoComplete="email"
                      autoFocus
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: '#9CA3AF' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                          '&:hover fieldset': { borderColor: 'primary.main' },
                        },
                        '& .MuiInputLabel-root': { color: '#9CA3AF' }
                      }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={resetLoading}
                      sx={{ 
                        mt: 4, 
                        mb: 2, 
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '1rem',
                        textTransform: 'none',
                        boxShadow: '0 4px 14px 0 rgba(15, 163, 163, 0.39)',
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(15, 163, 163, 0.23)',
                        }
                      }}
                    >
                      {resetLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t("auth.forgotPassword.send")}
                    </Button>
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => { setForgotMode(false); setResetSuccess(false); setResetError(null); }}
                  sx={{ color: '#9CA3AF', textTransform: 'none', mt: 1 }}
                >
                  {t("auth.forgotPassword.backToLogin")}
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white', fontWeight: 700 }}>
                  {t("auth.login.title")}
                </Typography>
                
                <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3 }}>
                  {t("auth.login.subtitle")}
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label={t("auth.login.emailLabel")}
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#9CA3AF' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover fieldset': { borderColor: 'primary.main' },
                      },
                      '& .MuiInputLabel-root': { color: '#9CA3AF' }
                    }}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label={t("auth.login.passwordLabel")}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: '#9CA3AF' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: '#9CA3AF' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover fieldset': { borderColor: 'primary.main' },
                      },
                      '& .MuiInputLabel-root': { color: '#9CA3AF' }
                    }}
                  />
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{ 
                      mt: 4, 
                      mb: 2, 
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: '1rem',
                      textTransform: 'none',
                      boxShadow: '0 4px 14px 0 rgba(15, 163, 163, 0.39)',
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(15, 163, 163, 0.23)',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t("auth.login.submit")}
                  </Button>

                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => setForgotMode(true)}
                    sx={{ color: '#9CA3AF', textTransform: 'none', textDecoration: 'underline' }}
                  >
                    {t("auth.forgotPassword.button")}
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default LoginPage;
